import { useLocalStorage } from '@vueuse/core'
import { supabase } from '@/supabase'

// The guest's contact email, captured early - it rides on the live-link request
// (anonymous tokens carry no email claim) and prefills account creation.
export const guest_email = useLocalStorage('guest_email', null)

// The location the guest is working on, so a refresh mid-funnel resumes.
export const guest_esiid = useLocalStorage('guest_esiid', null)

// The name the guest gave during the funnel (live-link form or bill scan), so
// account setup after conversion doesn't ask for it again.
export const guest_first_name = useLocalStorage('guest_first_name', null)
export const guest_last_name = useLocalStorage('guest_last_name', null)

// Saved right before signing in to an existing account: possession of this
// token is what lets the account claim the guest session's service locations.
export const guest_access_token = useLocalStorage('guest_access_token', null)

// Set when a returning customer is detected at the location step (before any
// guest data is built) and sent to sign in: the esiid they wanted to set up,
// so we can drop them on its best-plans page afterward.
export const guest_intent_esiid = useLocalStorage('guest_intent_esiid', null)

// Forgets what the funnel remembered about a guest. Everything above lives in
// this browser's local storage, so without this the next person on the device
// would find a stranger's email prefilled and a "welcome back" for a home that
// isn't theirs. Called when a new anonymous session is minted (whatever was
// remembered belonged to the old one), when a guest becomes an account (nothing
// left to carry), and on sign-out. The two hand-off values clear themselves
// when consumed; `handoff: false` leaves them for the claim that is about to
// read them.
export const clear_guest_session = ({ handoff = true } = {}) => {
  guest_email.value = null
  guest_esiid.value = null
  guest_first_name.value = null
  guest_last_name.value = null
  if (handoff) {
    guest_access_token.value = null
    guest_intent_esiid.value = null
  }
}

// Reuses whatever session already exists (a returning guest, or a full account
// that wandered in); only mints a fresh anonymous session when there is none -
// and then drops anything remembered by the session that came before it.
export const start_guest_session = async ({ captchaToken } = {}) => {
  const { data: { session: existing } } = await supabase.auth.getSession()
  if (existing) {
    return existing
  }
  clear_guest_session()
  const { data, error } = await supabase.auth.signInAnonymously({
    options: {
      ...(captchaToken && { captchaToken })
    }
  })
  if (error) {
    throw error
  }
  return data.session
}

export const save_guest_access_token = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  if (session?.user?.is_anonymous) {
    guest_access_token.value = session.access_token
  }
}

// Runs on any full-account sign-in (password, Google OAuth, a later visit):
// if a guest token was saved before the sign-in, hand the guest session's
// service locations to the account. One shot - the token is cleared before
// the call so a failure can't loop.
export const claim_pending_guest_session = async () => {
  const token = guest_access_token.value
  if (!token) {
    return
  }
  guest_access_token.value = null
  const { log_event } = await import('@/composables/helpers/datalayer_service')
  try {
    const { claim_guest_service_locations } = await import('@/services/service_api/service_locations')
    const result = await claim_guest_service_locations({ anonymous_access_token: token })
    log_event({ name: 'guest_funnel_claimed', claimed_count: result?.claimed_count })
  }
  catch (err) {
    // The sign-in itself stands; orders are matched by email regardless.
    log_event({ name: 'guest_funnel_claim_error', error_message: err.message })
  }
}

// After a returning customer signs in from the location-step nudge: make sure
// the location they wanted exists on their account (add it if not - idempotent,
// the API no-ops when it already exists), and return its esiid so the caller
// can route to its best-plans page. Returns null when there was no intent.
// One shot - the intent is cleared before any await so it can't loop.
export const consume_guest_intent = async () => {
  const esiid = guest_intent_esiid.value
  if (!esiid) {
    return null
  }
  guest_intent_esiid.value = null
  try {
    const { add_service_location_by_esiid } = await import('@/services/service_api/service_locations')
    await add_service_location_by_esiid({ esiid })
  }
  catch {
    // add is best-effort; the location may already exist. Route there anyway -
    // the plans page surfaces its own failure state if it truly isn't there.
  }
  return esiid
}
